import { Button, ButtonProps, Icon, Text } from "@ttc3k/trekker";
import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Copy } from "iconoir-react";
import { RecipeVariantProps } from "styled-system/types";
import { textRecipe } from "@ttc3k/trekker/dist/src/components/Typography/Text/recipe";

type CopyToClipboardProps = {
  text: string;
  iconOnly?: boolean;
  iconSize?: "sm" | "md" | "lg";
  iconCopyColor?: string;
  buttonProps?: ButtonProps;
  alternateDisplayText?: string;
} & RecipeVariantProps<typeof textRecipe>;

export const CopyToClipboard = ({
  text,
  iconOnly = false,
  iconSize = "sm",
  iconCopyColor = "icon.disabled",
  buttonProps,
  alternateDisplayText,
  ...props
}: CopyToClipboardProps) => {
  const [copied, setCopied] = useState(false);

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const [isHovered, setIsHovered] = useState(false);

  return (
    <Button
      visual="unstyled"
      display="grid"
      gridTemplateColumns={iconOnly ? "auto" : "auto 1fr"}
      alignItems="center"
      gap="50"
      borderRadius={25}
      onClick={handleCopyToClipboard}
      type="button"
      transitionProperty="color"
      transitionDuration="normal"
      transitionTimingFunction="default"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      {...buttonProps}
    >
      {!iconOnly && (
        <Text
          visual="smallMedium"
          color="text.mid"
          opacity={0.75}
          textOverflow={"ellipsis"}
          overflow="hidden"
          whiteSpace="nowrap"
          {...props}
          {...(isHovered && { ["data-hover"]: true })}
        >
          {alternateDisplayText ? alternateDisplayText : text}
        </Text>
      )}
      {copied ? (
        <motion.div
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0 }}
          transition={{
            type: "spring",
            bounce: true,
            stiffness: 500,
            damping: 20,
            duration: 0.2,
          }}
        >
          <Icon Element={Check} color="icon.success.mid" size={iconSize} />
        </motion.div>
      ) : (
        <Icon
          Element={Copy}
          color={iconCopyColor}
          size={iconSize}
          _hover={{
            color:
              iconCopyColor === "icon.disabled"
                ? "icon.light"
                : iconCopyColor === "icon.light"
                  ? "icon.mid"
                  : iconCopyColor === "icon.mid"
                    ? "icon.dark"
                    : "icon.mid",
          }}
          transitionProperty="color"
          transitionDuration="normal"
          transitionTimingFunction="default"
          {...(isHovered && { ["data-hover"]: true })}
        />
      )}
    </Button>
  );
};
